import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, startOfWeek, endOfWeek, addWeeks, addDays, getISOWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Sparkles, ChevronLeft, ChevronRight, Check, Lock, Plus, Download, Euro, CalendarDays, Save, Printer, X,
} from 'lucide-react';
import { cleaningAPI, roomsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';

const MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const eur = (v) => `${Number(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function Cleaning() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('rooms', 'edit');

  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [allRooms, setAllRooms] = useState([]);

  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [plan, setPlan] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Settings-Formular für den gewählten Standort
  const [settingsForm, setSettingsForm] = useState({ base_fee_monthly: '', billing_mode: 'per_room', provider_name: '', active: true, auto_only_priced: false });
  const [savingSettings, setSavingSettings] = useState(false);

  // Manuelles Hinzufügen
  const [manualRoomId, setManualRoomId] = useState('');
  const [manualDate, setManualDate] = useState('');

  const weekEnd = useMemo(() => endOfWeek(weekStart, { weekStartsOn: 1 }), [weekStart]);
  const startStr = format(weekStart, 'yyyy-MM-dd');
  const endStr = format(weekEnd, 'yyyy-MM-dd');

  const currentSettings = useMemo(
    () => locations.find((l) => l.location_id === locationId),
    [locations, locationId],
  );

  const loadSettingsList = useCallback(async () => {
    try {
      const res = await cleaningAPI.listSettings();
      setLocations(res.data || []);
      if (res.data?.length && !locationId) {
        setLocationId(res.data[0].location_id);
      }
    } catch {
      toast.error('Fehler beim Laden der Reinigungseinstellungen');
    }
  }, [locationId]);

  useEffect(() => { loadSettingsList(); }, [loadSettingsList]);

  // Settings-Formular füllen, wenn Standort/Settings wechseln
  useEffect(() => {
    if (currentSettings) {
      setSettingsForm({
        base_fee_monthly: currentSettings.base_fee_monthly ?? 0,
        billing_mode: currentSettings.billing_mode || 'per_room',
        provider_name: currentSettings.provider_name || '',
        active: currentSettings.active !== false,
        auto_only_priced: currentSettings.auto_only_priced === true,
      });
    }
  }, [currentSettings]);

  // Räume des Standorts laden (für manuelles Hinzufügen)
  useEffect(() => {
    if (!locationId) return;
    roomsAPI.getAll({ location_id: locationId })
      .then((res) => setAllRooms((res.data || []).filter((r) => r.is_active)))
      .catch(() => setAllRooms([]));
  }, [locationId]);

  const loadPlan = useCallback(async () => {
    if (!locationId) return;
    setLoadingPlan(true);
    try {
      const res = await cleaningAPI.getPlan({ location_id: locationId, start_date: startStr, end_date: endStr });
      setPlan(res.data);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const info = status ? `${status}${detail ? ` – ${detail}` : ''}` : (error.message || 'Netzwerkfehler');
      toast.error(`Fehler beim Laden des Putzplans (${info})`);
      console.error('[Putzplan] Fehler:', status, detail, error);
    } finally {
      setLoadingPlan(false);
    }
  }, [locationId, startStr, endStr]);

  const loadSummary = useCallback(async () => {
    if (!locationId) return;
    setLoadingSummary(true);
    try {
      const res = await cleaningAPI.getSummary({ location_id: locationId, year, month });
      setSummary(res.data);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const info = status
        ? `${status}${detail ? ` – ${detail}` : ''}`
        : (error.message || 'Netzwerkfehler');
      toast.error(`Fehler beim Laden der Kostenübersicht (${info})`);
      console.error('[Kostenübersicht] Fehler:', status, detail, error);
    } finally {
      setLoadingSummary(false);
    }
  }, [locationId, year, month]);

  useEffect(() => { loadPlan(); }, [loadPlan]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const handleSaveSettings = async () => {
    if (!locationId) return;
    setSavingSettings(true);
    try {
      await cleaningAPI.updateSettings(locationId, {
        base_fee_monthly: parseFloat(settingsForm.base_fee_monthly) || 0,
        billing_mode: settingsForm.billing_mode,
        provider_name: settingsForm.provider_name || null,
        active: settingsForm.active,
        auto_only_priced: settingsForm.auto_only_priced,
      });
      toast.success('Reinigungseinstellungen gespeichert');
      await loadSettingsList();
      loadSummary();
      loadPlan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleCell = async (room, date, cell) => {
    if (!canEdit) return;
    const dayInfo = plan.days.find((d) => d.date === date);
    if (dayInfo?.excluded) return;
    try {
      if (cell?.auto) {
        if (cell.excluded) {
          await cleaningAPI.includeAuto(room.id, date);
        } else {
          await cleaningAPI.excludeAuto({ room_id: room.id, date });
        }
      } else if (cell?.manual) {
        await cleaningAPI.removeManual(room.id, date);
      } else {
        await cleaningAPI.addManual({ room_id: room.id, date });
      }
      loadPlan();
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen');
    }
  };

  const handleAddManual = async () => {
    if (!manualRoomId || !manualDate) {
      toast.error('Bitte Raum und Tag wählen');
      return;
    }
    try {
      await cleaningAPI.addManual({ room_id: manualRoomId, date: manualDate });
      toast.success('Raum zur Reinigung hinzugefügt');
      setManualRoomId('');
      setManualDate('');
      loadPlan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Hinzufügen');
    }
  };

  const handlePrint = () => {
    if (!plan) return;
    const esc = (s) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const locName = currentSettings?.location_name || '';
    const kw = getISOWeek(weekStart);
    const title = `Putzplan KW ${kw} · ${format(weekStart, 'dd.MM.', { locale: de })} – ${format(weekEnd, 'dd.MM.yyyy', { locale: de })}`;

    const headCols = plan.days.map((d) =>
      `<th class="${d.excluded ? 'excl' : ''}">${d.weekday}<br><span class="dt">${d.date.slice(8, 10)}.${d.date.slice(5, 7)}.</span></th>`,
    ).join('') + '<th class="note">Bemerkung</th>';

    const rowsHtml = (plan.rooms || []).length === 0
      ? `<tr><td colspan="${plan.days.length + 2}" class="empty">Keine Räume zur Reinigung in dieser Woche.</td></tr>`
      : plan.rooms.map((room) => {
        const cells = plan.days.map((d) => {
          if (d.excluded) return '<td class="excl">–</td>';
          const cell = room.days[d.date];
          const counted = cell && !(cell.auto && cell.excluded);
          return `<td class="${counted ? 'mark' : ''}">${counted ? '✓' : ''}</td>`;
        }).join('');
        return `<tr><td class="room">${esc(room.name)}</td>${cells}<td class="note"></td></tr>`;
      }).join('');

    const html = `<!DOCTYPE html><html lang="de"><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  *{box-sizing:border-box;}
  body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:24px;}
  h1{font-size:20px;margin:0 0 4px;}
  .meta{font-size:13px;color:#555;margin-bottom:16px;}
  table{width:100%;border-collapse:collapse;font-size:13px;}
  th,td{border:1px solid #999;padding:8px 6px;text-align:center;}
  th{background:#1f2937;color:#fff;font-weight:600;}
  td.room{text-align:left;font-weight:600;white-space:nowrap;}
  td.mark{font-size:18px;font-weight:700;color:#047857;}
  th.excl,td.excl{background:#f3f4f6;color:#9ca3af;}
  td.note{min-width:140px;}
  th.note{min-width:140px;}
  .dt{font-weight:400;font-size:11px;}
  .empty{padding:24px;color:#777;}
  .legend{margin-top:14px;font-size:12px;color:#555;}
  .foot{margin-top:24px;font-size:11px;color:#999;}
  @media print{body{margin:12mm;} @page{size:portrait;}}
</style></head><body>
  <h1>${esc(title)}</h1>
  <div class="meta">${esc(locName)} · Wöchentlicher Putzplan</div>
  <table>
    <thead><tr><th style="text-align:left">Raum</th>${headCols}</tr></thead>
    <tbody>${rowsHtml}</tbody>
  </table>
  <div class="legend">✓ = an diesem Tag zu reinigen &nbsp;·&nbsp; grau = Wochenende/Feiertag/Schließtag (keine Reinigung)</div>
  <div class="foot">Erstellt am ${format(new Date(), 'dd.MM.yyyy HH:mm', { locale: de })}</div>
  <script>window.onload=function(){window.print();}<\/script>
</body></html>`;

    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Pop-up wurde blockiert. Bitte Pop-ups erlauben.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const handleExport = async () => {    try {
      const res = await cleaningAPI.exportSummary({ location_id: locationId, year, month });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reinigungskosten_${year}_${String(month).padStart(2, '0')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export fehlgeschlagen');
    }
  };

  const workingDaysOfWeek = useMemo(
    () => (plan?.days || []).filter((d) => !d.excluded),
    [plan],
  );

  const cellClasses = (d, cell) =>
    d.excluded ? 'bg-muted/30 text-muted-foreground/40 cursor-default'
      : (cell?.auto && cell?.excluded) ? 'bg-muted text-muted-foreground/70 line-through border border-dashed border-muted-foreground/40'
      : cell?.auto ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
      : cell?.manual ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-dashed border-blue-400'
      : canEdit ? 'hover:bg-muted border border-transparent hover:border-border' : 'bg-muted/20';

  const cellIcon = (d, cell, size = 'h-4 w-4') => {
    if (d.excluded) return <span className="text-muted-foreground/40">–</span>;
    if (cell?.auto && cell?.excluded) return <X className={size} />;
    if (cell?.auto || cell?.manual) return <Check className={size} />;
    return null;
  };

  const cellTitle = (d, cell) => {
    if (d.excluded) return d.reason;
    if (cell?.auto && cell?.excluded) return 'Ausgetragen – klicken, um wieder aufzunehmen';
    if (cell?.auto) return 'Automatisch (Einsatzplan/Buchung) – klicken zum Austragen';
    if (cell?.manual) return 'Manuell – klicken zum Entfernen';
    return 'Klicken zum manuellen Hinzufügen';
  };

  return (
    <DashboardLayout>
    <div className="space-y-6" data-testid="cleaning-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            Reinigung / Putzplan
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Reinigungskosten je Standort – automatisch aus Einsatzplan &amp; Raumbuchungen, plus manuelle Zusatz-Reinigungen.
          </p>
        </div>
        <div className="w-full sm:w-64">
          <Select value={locationId} onValueChange={setLocationId}>
            <SelectTrigger data-testid="cleaning-location-select">
              <SelectValue placeholder="Standort wählen..." />
            </SelectTrigger>
            <SelectContent>
              {locations.map((l) => (
                <SelectItem key={l.location_id} value={l.location_id}>{l.location_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Reinigungseinstellungen */}
      <Card className="border-border/60" data-testid="cleaning-settings-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Euro className="h-5 w-5" /> Reinigungseinstellungen (Standort)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Grundgebühr (€/Monat)</Label>
              <Input type="number" step="0.01" min="0" disabled={!canEdit}
                value={settingsForm.base_fee_monthly}
                onChange={(e) => setSettingsForm((p) => ({ ...p, base_fee_monthly: e.target.value }))}
                data-testid="cleaning-base-fee-input" />
            </div>
            <div className="space-y-2">
              <Label>Abrechnungsmodus</Label>
              <Select value={settingsForm.billing_mode} disabled={!canEdit}
                onValueChange={(v) => setSettingsForm((p) => ({ ...p, billing_mode: v }))}>
                <SelectTrigger data-testid="cleaning-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_room">Pro Raum (Grundgebühr + Tage × Preis)</SelectItem>
                  <SelectItem value="flat">Pauschal (nur Grundgebühr)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Dienstleister</Label>
              <Input disabled={!canEdit} value={settingsForm.provider_name}
                onChange={(e) => setSettingsForm((p) => ({ ...p, provider_name: e.target.value }))}
                placeholder="Optional" data-testid="cleaning-provider-input" />
            </div>
            <div className="flex items-center gap-3 sm:pt-7">
              <Switch checked={settingsForm.active} disabled={!canEdit}
                onCheckedChange={(v) => setSettingsForm((p) => ({ ...p, active: v }))}
                data-testid="cleaning-active-switch" />
              <Label>Aktiv</Label>
            </div>
          </div>
          <div className="flex items-start gap-3 mt-4 rounded-md border border-border p-3 bg-secondary/20">
            <Switch checked={settingsForm.auto_only_priced} disabled={!canEdit}
              onCheckedChange={(v) => setSettingsForm((p) => ({ ...p, auto_only_priced: v }))}
              data-testid="cleaning-auto-only-priced-switch" className="mt-0.5" />
            <div>
              <Label className="cursor-pointer">Nur Räume mit hinterlegtem Preis automatisch aufnehmen</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Räume ohne Reinigungspreis (z. B. in der allgemeinen Reinigung enthalten) werden nicht automatisch
                über Schichten/Buchungen eingeplant. Manuell hinzugefügte Tage bleiben davon unberührt.
              </p>
            </div>
          </div>
          {canEdit && (
            <div className="flex justify-end mt-4">
              <Button onClick={handleSaveSettings} disabled={savingSettings} data-testid="cleaning-save-settings-btn">
                <Save className="h-4 w-4 mr-2" /> Speichern
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="week">
        <TabsList>
          <TabsTrigger value="week" data-testid="cleaning-tab-week"><CalendarDays className="h-4 w-4 mr-2" />Wochenplan</TabsTrigger>
          <TabsTrigger value="month" data-testid="cleaning-tab-month"><Euro className="h-4 w-4 mr-2" />Monatsübersicht</TabsTrigger>
        </TabsList>

        {/* ===== Wochenplan ===== */}
        <TabsContent value="week" className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <Button variant="outline" size="sm" onClick={() => setWeekStart((w) => addWeeks(w, -1))} data-testid="cleaning-week-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="font-medium text-sm" data-testid="cleaning-week-label">
                {format(weekStart, 'dd.MM.', { locale: de })} – {format(weekEnd, 'dd.MM.yyyy', { locale: de })}
              </div>
              <Button variant="outline" size="sm" onClick={handlePrint} title="Wochenplan drucken / als PDF" data-testid="cleaning-print-btn">
                <Printer className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Drucken</span>
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={() => setWeekStart((w) => addWeeks(w, 1))} data-testid="cleaning-week-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <Card className="border-border/60 hidden md:block">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="cleaning-week-table">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium sticky left-0 bg-muted/50 min-w-[180px]">Raum</th>
                    {(plan?.days || []).map((d) => (
                      <th key={d.date} className={`p-2 font-medium text-center min-w-[64px] ${d.excluded ? 'text-muted-foreground/60' : ''}`}>
                        <div>{d.weekday}</div>
                        <div className="text-[11px] font-normal">{d.date.slice(8, 10)}.{d.date.slice(5, 7)}.</div>
                      </th>
                    ))}
                    <th className="p-2 font-medium text-center">Σ</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingPlan ? (
                    <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">Lädt…</td></tr>
                  ) : (plan?.rooms || []).length === 0 ? (
                    <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">
                      Diese Woche sind keine Räume zur Reinigung vorgemerkt.
                    </td></tr>
                  ) : (
                    plan.rooms.map((room) => (
                      <tr key={room.id} className="border-t" data-testid={`cleaning-room-row-${room.id}`}>
                        <td className="p-3 font-medium sticky left-0 bg-background">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: room.color }} />
                            {room.name}
                            {room.cleaning_price_per_day != null && (
                              <span className="text-[11px] text-muted-foreground">({eur(room.cleaning_price_per_day)}/Tag)</span>
                            )}
                          </div>
                        </td>
                        {plan.days.map((d) => {
                          const cell = room.days[d.date];
                          return (
                            <td key={d.date} className="p-1 text-center">
                              <button
                                type="button"
                                disabled={d.excluded || !canEdit}
                                onClick={() => toggleCell(room, d.date, cell)}
                                title={cellTitle(d, cell)}
                                className={`w-8 h-8 rounded-md inline-flex items-center justify-center transition-colors ${cellClasses(d, cell)}`}
                                data-testid={`cleaning-cell-${room.id}-${d.date}`}
                              >
                                {cellIcon(d, cell)}
                              </button>
                            </td>
                          );
                        })}
                        <td className="p-2 text-center font-semibold" data-testid={`cleaning-room-total-${room.id}`}>{room.total_days}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Mobile: Karten-/Chip-Ansicht statt breiter Tabelle */}
          <div className="md:hidden space-y-3">
            {loadingPlan ? (
              <Card><CardContent className="p-6 text-center text-muted-foreground">Lädt…</CardContent></Card>
            ) : (plan?.rooms || []).length === 0 ? (
              <Card><CardContent className="p-6 text-center text-muted-foreground text-sm">
                Diese Woche sind keine Räume zur Reinigung vorgemerkt.
              </CardContent></Card>
            ) : (
              plan.rooms.map((room) => (
                <Card key={room.id} data-testid={`cleaning-room-card-${room.id}`}>
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: room.color }} />
                        <span className="font-medium truncate">{room.name}</span>
                      </div>
                      <Badge variant="outline" className="shrink-0">{room.total_days} Tag{room.total_days !== 1 ? 'e' : ''}</Badge>
                    </div>
                    {room.cleaning_price_per_day != null && (
                      <div className="text-[11px] text-muted-foreground">{eur(room.cleaning_price_per_day)}/Tag</div>
                    )}
                    <div className="grid grid-cols-7 gap-1">
                      {plan.days.map((d) => {
                        const cell = room.days[d.date];
                        return (
                          <button
                            key={d.date}
                            type="button"
                            disabled={d.excluded || !canEdit}
                            onClick={() => toggleCell(room, d.date, cell)}
                            title={cellTitle(d, cell)}
                            className={`flex flex-col items-center justify-center rounded-md py-1.5 transition-colors ${cellClasses(d, cell)}`}
                            data-testid={`cleaning-cell-m-${room.id}-${d.date}`}
                          >
                            <span className="text-[11px] font-medium">{d.weekday}</span>
                            <span className="text-[10px] leading-none">{d.date.slice(8, 10)}.</span>
                            <span className="mt-0.5 h-3 flex items-center justify-center">{cellIcon(d, cell, 'h-3 w-3')}</span>
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded bg-emerald-100 dark:bg-emerald-900/30 inline-flex items-center justify-center"><Check className="h-3 w-3 text-emerald-700 dark:text-emerald-300" /></span> Automatisch (Einsatz/Buchung)</span>
            <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded bg-blue-100 dark:bg-blue-900/30 border border-dashed border-blue-400 inline-flex items-center justify-center"><Check className="h-3 w-3 text-blue-700 dark:text-blue-300" /></span> Manuell hinzugefügt</span>
            <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded bg-muted border border-dashed border-muted-foreground/40 inline-flex items-center justify-center"><X className="h-3 w-3 text-muted-foreground" /></span> Ausgetragen (nicht reinigen)</span>
            <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Wochenende/Feiertag/Schließtag = ausgenommen</span>
          </div>

          {/* Manuell Raum hinzufügen */}
          {canEdit && (
            <Card className="border-dashed">
              <CardContent className="py-4">
                <Label className="text-sm font-medium flex items-center gap-2 mb-3"><Plus className="h-4 w-4" />Raum zusätzlich reinigen lassen</Label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Select value={manualRoomId} onValueChange={setManualRoomId}>
                    <SelectTrigger className="sm:w-64" data-testid="cleaning-manual-room-select">
                      <SelectValue placeholder="Raum wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allRooms.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={manualDate} onValueChange={setManualDate}>
                    <SelectTrigger className="sm:w-56" data-testid="cleaning-manual-date-select">
                      <SelectValue placeholder="Tag wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {workingDaysOfWeek.map((d) => (
                        <SelectItem key={d.date} value={d.date}>{d.weekday}, {d.date.slice(8, 10)}.{d.date.slice(5, 7)}.</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleAddManual} data-testid="cleaning-manual-add-btn">
                    <Plus className="h-4 w-4 mr-2" />Hinzufügen
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===== Monatsübersicht ===== */}
        <TabsContent value="month" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
              <SelectTrigger className="w-40" data-testid="cleaning-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS_DE.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-28" data-testid="cleaning-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[year - 1, year, year + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleExport} className="ml-auto" data-testid="cleaning-export-btn">
              <Download className="h-4 w-4 mr-2" />Excel-Export
            </Button>
          </div>

          {loadingSummary ? (
            <div className="p-8 text-center text-muted-foreground">Lädt…</div>
          ) : summary && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card><CardContent className="p-4">
                  <div className="text-xs text-muted-foreground">Abrechnungsmodus</div>
                  <div className="text-lg font-semibold mt-1">
                    {summary.billing_mode === 'per_room'
                      ? <Badge variant="outline">Pro Raum</Badge>
                      : <Badge variant="outline">Pauschal</Badge>}
                  </div>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                  <div className="text-xs text-muted-foreground">Grundgebühr</div>
                  <div className="text-lg font-semibold mt-1" data-testid="cleaning-summary-base">{eur(summary.base_fee_monthly)}</div>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                  <div className="text-xs text-muted-foreground">Σ Raum-Kosten ({summary.total_room_days} Raum-Tage)</div>
                  <div className="text-lg font-semibold mt-1">{eur(summary.rooms_cost_total)}</div>
                </CardContent></Card>
                <Card className="border-primary/40 bg-primary/5"><CardContent className="p-4">
                  <div className="text-xs text-muted-foreground">Gesamtkosten</div>
                  <div className="text-2xl font-bold mt-1 text-primary" data-testid="cleaning-summary-total">{eur(summary.total_cost)}</div>
                </CardContent></Card>
              </div>

              <Card className="border-border/60">
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">Raum</th>
                        <th className="text-right p-3 font-medium">Preis/Tag</th>
                        <th className="text-right p-3 font-medium">Gereinigte Tage</th>
                        <th className="text-right p-3 font-medium">Kosten</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.rooms.length === 0 ? (
                        <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">Keine gereinigten Räume in diesem Monat.</td></tr>
                      ) : summary.rooms.map((r) => (
                        <tr key={r.room_id} className="border-t" data-testid={`cleaning-summary-row-${r.room_id}`}>
                          <td className="p-3 font-medium">{r.name}</td>
                          <td className="p-3 text-right">{eur(r.price_per_day)}</td>
                          <td className="p-3 text-right">{r.cleaned_days}</td>
                          <td className="p-3 text-right font-medium">{summary.billing_mode === 'per_room' ? eur(r.cost) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
              {summary.billing_mode === 'flat' && (
                <p className="text-xs text-muted-foreground">Pauschal-Abrechnung: Es fällt nur die monatliche Grundgebühr an, unabhängig von der Anzahl gereinigter Räume.</p>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
    </DashboardLayout>
  );
}
